import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../App';
import { adminApi } from '../../lib/api';
import AdminLayout from '../../components/AdminLayout';
import { formatCurrency } from '../../lib/utils';
import { Skeleton } from '../../components/ui/skeleton';
import { toast } from 'sonner';
import { 
  Users, TrendingUp, Wallet, AlertTriangle, FileText, CreditCard,
  ArrowUpRight, ArrowDownRight
} from 'lucide-react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip } from 'recharts';

const AdminDashboardPage = () => {
  const { adminUser } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      const res = await adminApi.getDashboard();
      setStats(res.data);
    } catch (error) {
      toast.error('Failed to load dashboard');
    } finally {
      setIsLoading(false);
    }
  };

  const StatCard = ({ icon: Icon, label, value, trend, trendUp, color = 'text-primary' }) => (
    <div className="bg-card border border-white/10 rounded-xl p-6">
      <div className="flex items-center justify-between mb-4">
        <div className={`w-10 h-10 rounded-lg ${color.replace('text-', 'bg-')}/20 flex items-center justify-center`}>
          <Icon className={`w-5 h-5 ${color}`} />
        </div>
        {trend && (
          <div className={`flex items-center gap-1 text-sm ${trendUp ? 'text-green-500' : 'text-red-500'}`}>
            {trendUp ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
            {trend}%
          </div>
        )}
      </div>
      <p className="text-2xl font-bold mb-1">{value}</p>
      <p className="text-sm text-muted-foreground">{label}</p>
    </div>
  );

  if (isLoading) {
    return (
      <AdminLayout>
        <div className="p-6 space-y-6">
          <Skeleton className="h-8 w-48" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3, 4, 5, 6].map(i => (
              <Skeleton key={i} className="h-32 rounded-xl" />
            ))}
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Skeleton className="h-64 rounded-xl" />
            <Skeleton className="h-64 rounded-xl" />
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-6">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Dashboard
          </h1>
          <p className="text-muted-foreground">
            Welcome back, {adminUser?.name}
          </p>
        </div>
        
        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
          <StatCard 
            icon={Users} 
            label="Total Users" 
            value={stats?.total_users?.toLocaleString() || 0}
            trend={12}
            trendUp
          />
          <StatCard 
            icon={TrendingUp} 
            label="Active Creators" 
            value={stats?.active_creators?.toLocaleString() || 0}
            color="text-green-500"
            trend={8}
            trendUp
          />
          <StatCard 
            icon={Wallet} 
            label="Total Balance Held" 
            value={formatCurrency(stats?.total_balance_held || 0)}
            color="text-blue-500"
          />
          <StatCard 
            icon={CreditCard} 
            label="Payouts This Month" 
            value={formatCurrency(stats?.payouts_this_month || 0)}
            color="text-yellow-500"
          />
          <StatCard 
            icon={FileText} 
            label="Pending KYC Reviews" 
            value={stats?.pending_kyc_reviews || 0}
            color="text-purple-500"
          />
          <StatCard 
            icon={AlertTriangle} 
            label="Fraud Alerts" 
            value={stats?.fraud_alerts || 0}
            color="text-red-500"
          />
        </div>
        
        {/* Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Daily Transactions Chart */}
          <div className="bg-card border border-white/10 rounded-xl p-6">
            <h3 className="font-semibold mb-4">Daily Transactions</h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={stats?.daily_transactions || []}>
                  <XAxis 
                    dataKey="date" 
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: '#6b7280', fontSize: 10 }}
                    tickFormatter={(value) => new Date(value).getDate()}
                  />
                  <YAxis 
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: '#6b7280', fontSize: 10 }}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#1a1a1a', 
                      border: '1px solid rgba(255,255,255,0.1)',
                      borderRadius: '8px'
                    }}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="count" 
                    stroke="#E02020" 
                    strokeWidth={2}
                    dot={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
          
          {/* Wallet Activations Chart */}
          <div className="bg-card border border-white/10 rounded-xl p-6">
            <h3 className="font-semibold mb-4">Wallet Activations</h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={stats?.wallet_activations || []}>
                  <XAxis 
                    dataKey="date" 
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: '#6b7280', fontSize: 10 }}
                    tickFormatter={(value) => new Date(value).getDate()}
                  />
                  <YAxis 
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: '#6b7280', fontSize: 10 }}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#1a1a1a', 
                      border: '1px solid rgba(255,255,255,0.1)',
                      borderRadius: '8px'
                    }}
                  />
                  <Bar dataKey="count" fill="#E02020" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
        
        {/* Quick Actions */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Link
            to="/admin/campaigns"
            className="bg-card border border-white/10 rounded-xl p-4 hover:border-primary/50 transition-colors text-center"
          >
            <TrendingUp className="w-8 h-8 mx-auto mb-2 text-primary" />
            <p className="font-medium">Manage Campaigns</p>
          </Link>
          <Link
            to="/admin/payouts"
            className="bg-card border border-white/10 rounded-xl p-4 hover:border-primary/50 transition-colors text-center"
          >
            <CreditCard className="w-8 h-8 mx-auto mb-2 text-green-500" />
            <p className="font-medium">Process Payouts</p>
          </Link>
          <Link
            to="/admin/kyc"
            className="bg-card border border-white/10 rounded-xl p-4 hover:border-primary/50 transition-colors text-center"
          >
            <FileText className="w-8 h-8 mx-auto mb-2 text-purple-500" />
            <p className="font-medium">Review KYC</p>
          </Link>
          <Link
            to="/admin/users"
            className="bg-card border border-white/10 rounded-xl p-4 hover:border-primary/50 transition-colors text-center"
          >
            <Users className="w-8 h-8 mx-auto mb-2 text-blue-500" />
            <p className="font-medium">Manage Users</p>
          </Link>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminDashboardPage;
